import { Modal } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { IEcgRecordingSummary } from "../../../../@types/Ecg";
import { Close } from "../../../../assets/icons/Close";
import { EcgChart, EcgChartSkeleton } from "../../../../components/EcgChart";
import {
  AMPLITUDE_GAINS,
  DEFAULT_AMPLITUDE_GAIN,
  DEFAULT_PAPER_SPEED,
  DEFAULT_ZOOM,
  PAPER_SPEEDS,
  ZOOM_LEVELS,
} from "../../../../components/EcgChart/ecgChart.constants";
import { useEcgWaveform } from "../../../../hooks/queries/ecg";
import {
  getLongFormattedDate,
  getShortFormattedDate,
} from "../../../../utils/dateFormatter";
import { getEcgClassificationLabel } from "../../../../utils/ecg/ecgClassification";
import { displayValue } from "../../../../utils/stringFormatter";
import { EcgControlSelect } from "./EcgControlSelect";
import {
  ControlsRow,
  ecgTracingModalCss,
  GraphArea,
  GraphMessage,
  InfoBar,
  InfoItem,
  ModalBody,
  TitleBlock,
} from "./EcgTracingModal.styles";

export type EcgTracingModalProps = {
  summary: IEcgRecordingSummary | null;
  open: boolean;
  onClose: () => void;
};

export function EcgTracingModal({
  summary,
  open,
  onClose,
}: EcgTracingModalProps) {
  const { t } = useTranslation();

  const [paperSpeed, setPaperSpeed] = useState<number>(DEFAULT_PAPER_SPEED);
  const [amplitudeGain, setAmplitudeGain] = useState<number>(
    DEFAULT_AMPLITUDE_GAIN
  );
  const [zoom, setZoom] = useState<number>(DEFAULT_ZOOM);

  // Samples are fetched lazily from the recording's (short-lived) waveform URL.
  const { recording, isLoading, isError } = useEcgWaveform(summary);

  const recordedAt = summary?.recordedAt ?? "";

  const title = summary ? (
    <TitleBlock>
      <span className="title">{t("patientPage_ecg_recording")}</span>
      <span className="subtitle">{getLongFormattedDate(recordedAt, "-")}</span>
    </TitleBlock>
  ) : (
    t("patientPage_ecg_recording")
  );

  const renderGraph = () => {
    if (isLoading)
      return (
        <EcgChartSkeleton
          leadCount={summary?.leadCount}
          durationSeconds={summary?.durationSeconds}
        />
      );
    if (isError)
      return (
        <GraphMessage>{t("patientPage_ecg_tracingLoadError")}</GraphMessage>
      );
    if (!recording) return null;

    return (
      <EcgChart
        recording={recording}
        paperSpeed={paperSpeed}
        amplitudeGain={amplitudeGain}
        zoom={zoom}
      />
    );
  };

  return (
    <Modal
      title={title}
      open={open && !!summary}
      onCancel={onClose}
      footer={null}
      width="92vw"
      style={{ maxWidth: "1280px" }}
      zIndex={3001}
      styles={{ mask: { pointerEvents: "all" } }}
      className={ecgTracingModalCss()}
      closeIcon={<Close />}
      centered
      destroyOnHidden
    >
      {summary && (
        <ModalBody>
          <ControlsRow>
            <EcgControlSelect
              label={t("patientPage_ecg_speed")}
              value={paperSpeed}
              suffix=" mm/s"
              options={PAPER_SPEEDS}
              onChange={setPaperSpeed}
            />
            <EcgControlSelect
              label={t("patientPage_ecg_gain")}
              value={amplitudeGain}
              suffix=" mm/mV"
              options={AMPLITUDE_GAINS}
              onChange={setAmplitudeGain}
            />
            <EcgControlSelect
              label={t("patientPage_ecg_zoom")}
              value={zoom}
              suffix="x"
              options={ZOOM_LEVELS}
              onChange={setZoom}
            />
          </ControlsRow>

          <GraphArea>{renderGraph()}</GraphArea>

          <InfoBar>
            <InfoItem>
              <span className="value">{summary.durationSeconds} s</span>
              <span className="label">{t("patientPage_ecg_duration")}</span>
              <span className="date">{getShortFormattedDate(recordedAt)}</span>
            </InfoItem>
            <InfoItem>
              <span className="value">{summary.leadCount}</span>
              <span className="label">{t("patientPage_ecg_lead")}</span>
              <span className="date">{getShortFormattedDate(recordedAt)}</span>
            </InfoItem>
            <InfoItem>
              <span className="value">{summary.sampleRateHz} Hz</span>
              <span className="label">{t("patientPage_ecg_sampleRate")}</span>
              <span className="date">{getShortFormattedDate(recordedAt)}</span>
            </InfoItem>
            {summary.averageHeartRateBpm != null && (
              <InfoItem>
                <span className="value">
                  {`${summary.averageHeartRateBpm} bpm`}
                </span>
                <span className="label">{t("patientPage_ecg_heartRate")}</span>
                <span className="date">
                  {getShortFormattedDate(recordedAt)}
                </span>
              </InfoItem>
            )}
            <InfoItem>
              <span className="value">
                {getEcgClassificationLabel(summary.classification)}
              </span>
              <span className="label">{t("patientPage_ecg_status")}</span>
              <span className="date">{getShortFormattedDate(recordedAt)}</span>
            </InfoItem>
            {summary.batteryPercent != null && (
              <InfoItem>
                <span className="value">{`${summary.batteryPercent}%`}</span>
                <span className="label">{t("patientPage_ecg_battery")}</span>
                <span className="date">
                  {getShortFormattedDate(recordedAt)}
                </span>
              </InfoItem>
            )}
            {summary.platform ? (
              <InfoItem>
                <span className="value">{displayValue(summary.platform)}</span>
                <span className="label">{t("patientPage_ecg_recorder")}</span>
                <span className="date">
                  {getShortFormattedDate(recordedAt)}
                </span>
              </InfoItem>
            ) : null}
          </InfoBar>
        </ModalBody>
      )}
    </Modal>
  );
}
